import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';

const AddPositionDialog = ({ show, onClose, tradeId, symbolId, symbolName, onPositionAdded }) => {
  const [exchanges, setExchanges] = useState([]);
  const [formData, setFormData] = useState({
    exchangeId: '',
    positionType: 'LONG'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch list of exchanges when dialog opens
  useEffect(() => {
    if (show) {
      fetchExchanges();
      // Reset form when dialog opens
      setFormData({
        exchangeId: '',
        positionType: 'LONG'
      });
      setError(null);
    }
  }, [show]);

  const fetchExchanges = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/exchanges`);
      const data = await response.json();
      if (data.success) {
        setExchanges(data.data);
      }
    } catch (error) {
      console.error('Error fetching exchanges:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.exchangeId) {
      setError('Please select an exchange');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}/positions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exchange_id: parseInt(formData.exchangeId),
          symbol_id: symbolId,
          contract_type: formData.positionType
        })
      });

      const data = await response.json();

      if (data.success) {
        // Notify parent component that position was added
        if (onPositionAdded) {
          onPositionAdded(data.data);
        }
        onClose();
      } else {
        setError(data.error || 'Failed to add position');
      }
    } catch (error) {
      console.error('Error adding position:', error);
      setError('Error adding position. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#0d1117',
        border: '1px solid #30363d',
        borderRadius: '6px',
        padding: '24px',
        minWidth: '400px',
        maxWidth: '500px',
        color: '#c9d1d9'
      }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#ffffff' }}>Add New Position</h3>

        <form onSubmit={handleSubmit}>
          {/* Symbol (read-only) */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              Symbol
            </label>
            <input
              type="text"
              value={symbolName || ''}
              readOnly
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#161b22',
                border: '1px solid #30363d',
                borderRadius: '4px',
                color: '#8b949e',
                fontSize: '14px',
                cursor: 'not-allowed'
              }}
            />
          </div>

          {/* Exchange */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              Exchange *
            </label>
            <select
              value={formData.exchangeId}
              onChange={(e) => handleChange('exchangeId', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#0d1117',
                border: '1px solid #30363d',
                borderRadius: '4px',
                color: '#c9d1d9',
                fontSize: '14px'
              }}
            >
              <option value="">Select exchange...</option>
              {exchanges.map(exchange => (
                <option key={exchange.id} value={exchange.id}>
                  {exchange.exchange_name}
                </option>
              ))}
            </select>
          </div>

          {/* Position Type */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              Position Type *
            </label>
            <select
              value={formData.positionType}
              onChange={(e) => handleChange('positionType', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#0d1117',
                border: '1px solid #30363d',
                borderRadius: '4px',
                color: '#c9d1d9',
                fontSize: '14px'
              }}
            >
              <option value="SPOT">SPOT</option>
              <option value="LONG">LONG</option>
              <option value="SHORT">SHORT</option>
            </select>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              background: '#f851491a',
              border: '1px solid #f85149',
              borderRadius: '4px',
              color: '#f85149',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '8px 16px',
                background: '#21262d',
                border: '1px solid #30363d',
                borderRadius: '4px',
                color: '#c9d1d9',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '8px 16px',
                background: '#238636',
                border: '1px solid #2ea043',
                borderRadius: '4px',
                color: '#ffffff',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Adding...' : 'Add Position'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPositionDialog;
